import type { WorkItemStatus, WorkItemSummary } from "../../shared/types/api";
import { STATUS_ORDER } from "./workItemVocabulary";

/** One project present in a list of work, with how much of that list belongs to it. */
export interface ProjectTally {
  id: string;
  key: string;
  name: string;
  count: number;
}

/** A status heading and the rows under it. */
export interface StatusGroup {
  status: WorkItemStatus;
  items: WorkItemSummary[];
}

/**
 * The projects a list of work actually touches.
 *
 * <p>Built from the items rather than from the project list, because a filter offering projects
 * that would return nothing is a filter that mostly disappoints. Someone with work in two
 * projects should see two choices, not forty.
 */
export function projectTally(items: WorkItemSummary[]): ProjectTally[] {
  const byId = new Map<string, ProjectTally>();

  for (const item of items) {
    const existing = byId.get(item.projectId);
    if (existing) {
      existing.count += 1;
    } else {
      byId.set(item.projectId, {
        id: item.projectId,
        key: item.projectKey,
        name: item.projectName,
        count: 1,
      });
    }
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Work split into status headings, in the board's column order.
 *
 * <p>Statuses nothing sits in are left out entirely. A page of empty headings reads as though
 * something failed to load.
 */
export function groupByStatus(items: WorkItemSummary[]): StatusGroup[] {
  const byStatus = new Map<WorkItemStatus, WorkItemSummary[]>();

  for (const item of items) {
    const existing = byStatus.get(item.status);
    if (existing) {
      existing.push(item);
    } else {
      byStatus.set(item.status, [item]);
    }
  }

  return STATUS_ORDER.filter((status) => byStatus.has(status)).map((status) => ({
    status,
    items: byStatus.get(status) ?? [],
  }));
}
